import { QualityControl } from '../../domain/entities/quality-control.entity';
import { QcAsset } from '../../domain/entities/quality-control-asset.entity';
import { EditAssetItemDto } from '../../application/dtos/edit-assets.dto';
import { QualityControlStatus } from '../../domain/value-objects/quality-control-status.vo';
import {
  QcValidations,
  QcAppraisalValidation,
  QcAssetValidation,
  QcVerification,
} from '../../domain/entities/quality-control-validation.entity';
import { RevisionResult } from '../../domain/value-objects/revision-result.vo';
import { PropertyRegisterValidationResult } from '../../domain/value-objects/property-register-validation-result.vo';

export interface QualityControlListItemApiResponse {
  id: string;
  version: number;
  createdAt: string;
  statusValue: number;
  appraisalName: string | null;
  technicalUserIds: string | null;
  orderId: string | null;
  code: string | null;
  finishedAt: string | null;
  qualityControlResult: number | null;
  isCancelled?: boolean;
  userName?: string | null;
}

export interface QualityControlDetailApiResponse {
  id: string;
  version?: number;
  createdAt?: string;
  statusValue?: number;
  appraisalName?: string | null;
  orderId?: string | null;
  appraisalCompany: string | null;
  appraisalPurpose: string | null;
  finishedAt: string | null;
  qualityControlResult: number | null;
  assignedTechnicalUserFullName?: string | null;
}

export interface QualityControlListPagedApiResponse {
  items: QualityControlListItemApiResponse[];
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  totalItemsCount: number;
}

// ─── Validations API types ────────────────────────────────────────────────────

export interface QcVerificationApiResponse {
  id: string;
  result: number;
  koResult: number | null;
  observations: string | null;
}

export interface QcAppraisalValidationApiResponse {
  totalValue: number | null;
  estimatedValue: number | null;
  deviation: number | null;
  qualityControlResult: number | null;
  verifications: QcVerificationApiResponse[] | null;
}

export interface QcAssetValidationApiResponse {
  id: string;
  name: string | null;
  propertyRegistryNumber: string | null;
  totalValue: number | null;
  estimatedValue: number | null;
  deviation: number | null;
  revisionResult: number | null;
  verifications: QcVerificationApiResponse[] | null;
}

export interface QcValidationsApiResponse {
  qualityControlValidation: QcAppraisalValidationApiResponse | null;
  assetsValidations: QcAssetValidationApiResponse[] | null;
}

// ─── Assets API types ─────────────────────────────────────────────────────────

export interface QcAssetApiResponse {
  id: string;
  assetName: string | null;
  propertyRegistryNumber: string | null;
  propertyRegistry: string | null;
  assetUse: string | null;
  cadastralReferences: string[] | null;
  idufir: string | null;
  adoptedValorationMethod: number | null;
  adoptedSurfaceValue: number | null;
  adoptedSurface: number | null;
  unitValue: number | null;
  totalValue: number | null;
}

export interface QcAssetsApiResponse {
  items: QcAssetApiResponse[] | null;
}

export class QualityControlMapper {
  static toDomain(raw: QualityControlDetailApiResponse): QualityControl {
    return {
      id: raw.id,
      createdAt: raw.createdAt ? new Date(raw.createdAt) : new Date(0),
      version: raw.version ?? 0,
      statusValue: raw.statusValue != null ? raw.statusValue as QualityControlStatus : QualityControlStatus.PendingAssignment,
      appraisalName: raw.appraisalName ?? null,
      technicalUserIds: raw.assignedTechnicalUserFullName ?? null,
      assignedTechnicalUserFullName: raw.assignedTechnicalUserFullName ?? null,
      orderId: raw.orderId ?? null,
      code: null,
      appraisalCompany: raw.appraisalCompany,
      appraisalPurpose: raw.appraisalPurpose,
      finishedAt: raw.finishedAt ? new Date(raw.finishedAt) : null,
      qualityControlResult: raw.qualityControlResult as RevisionResult | null,
    };
  }

  static toDomainFromList(raw: QualityControlListItemApiResponse): QualityControl {
    return {
      id: raw.id,
      createdAt: new Date(raw.createdAt),
      version: raw.version,
      statusValue: raw.statusValue as QualityControlStatus,
      appraisalName: raw.appraisalName,
      technicalUserIds: raw.technicalUserIds,

      assignedTechnicalUserFullName: raw.userName ?? null,

      orderId: raw.orderId,
      code: raw.code ?? null,
      appraisalCompany: null,
      appraisalPurpose: null,
      finishedAt: raw.finishedAt ? new Date(raw.finishedAt) : null,
      qualityControlResult: raw.qualityControlResult as RevisionResult | null,
      isCancelled: raw.isCancelled ?? false,
    };
  }

  static toDomainList(rawList: QualityControlListItemApiResponse[]): QualityControl[] {
    return rawList.map((raw) => this.toDomainFromList(raw));
  }

  // ─── Validations ────────────────────────────────────────────────────────────

  private static mapVerification(raw: QcVerificationApiResponse): QcVerification {
    return {
      id: raw.id,
      result: raw.result as PropertyRegisterValidationResult,
      koResult: raw.koResult,
      observations: raw.observations,
    };
  }

  private static mapAppraisalValidation(raw: QcAppraisalValidationApiResponse | null): QcAppraisalValidation {
    if (!raw) {
      return { totalValue: null, estimatedValue: null, deviation: null, qualityControlResult: null, verifications: [] };
    }
    return {
      totalValue: raw.totalValue,
      estimatedValue: raw.estimatedValue,
      deviation: raw.deviation,
      qualityControlResult: raw.qualityControlResult as RevisionResult | null,
      verifications: (raw.verifications ?? []).map((v) => this.mapVerification(v)),
    };
  }

  private static mapAssetValidation(raw: QcAssetValidationApiResponse): QcAssetValidation {
    return {
      id: raw.id,
      name: raw.name,
      propertyRegistryNumber: raw.propertyRegistryNumber,
      totalValue: raw.totalValue,
      estimatedValue: raw.estimatedValue,
      deviation: raw.deviation,
      revisionResult: raw.revisionResult as RevisionResult | null,
      verifications: (raw.verifications ?? []).map((v) => this.mapVerification(v)),
    };
  }

  static toValidationsDomain(raw: QcValidationsApiResponse): QcValidations {
    return {
      qualityControlValidation: this.mapAppraisalValidation(raw.qualityControlValidation),
      assetsValidations: (raw.assetsValidations ?? []).map((av) => this.mapAssetValidation(av)),
    };
  }

  // ─── Assets ──────────────────────────────────────────────────────────────────

  static toAssetsDomain(raw: QcAssetsApiResponse): QcAsset[] {
    return (raw.items ?? []).map((item) => ({
      id: item.id,
      assetName: item.assetName,
      propertyRegistryNumber: item.propertyRegistryNumber,
      propertyRegistry: item.propertyRegistry,
      assetUse: item.assetUse,
      cadastralReferences: item.cadastralReferences,
      idufir: item.idufir,
      adoptedValorationMethod: item.adoptedValorationMethod,
      adoptedSurfaceValue: item.adoptedSurfaceValue,
      adoptedSurface: item.adoptedSurface,
      unitValue: item.unitValue,
      totalValue: item.totalValue,
    }));
  }

  static toAssetTableRows(items: QcAsset[]): Record<string, string>[] {
    return items.map((asset) => ({
      assetName: asset.assetName ?? '',
      propertyRegistryNumber: asset.propertyRegistryNumber ?? '',
      propertyRegistryEnumValue: asset.propertyRegistry ?? '',
      assetUse: asset.assetUse ?? '',
      cadastralReferences: asset.cadastralReferences?.join(', ') ?? '',
      idufir: asset.idufir ?? '',
      adoptedValorationMethod: asset.adoptedValorationMethod != null ? String(asset.adoptedValorationMethod) : '',
      adoptedSurfaceValue: asset.adoptedSurfaceValue != null ? String(asset.adoptedSurfaceValue) : '',
      adoptedSurface: asset.adoptedSurface != null ? String(asset.adoptedSurface) : '',
      unitValue: asset.unitValue != null ? String(asset.unitValue) : '',
      totalValue: asset.totalValue != null ? String(asset.totalValue) : '',
    }));
  }

  static sortFieldToApi(field: string): string {
    const mapping: Record<string, string> = {
      orderId: 'orderid',
      code: 'code',
      version: 'version',
      appraisalName: 'appraisalname',
      createdAt: 'createdat',
      statusValue: 'status',
      technicalUserIds: 'technicalUserIds',
      finishedAt: 'finishedat',
      qualityControlResult: 'qualitycontrolresult',
    };
    return mapping[field] ?? field.toLowerCase();
  }

  static dateFilterToIso(value: unknown): string {
    const date = value instanceof Date ? value : new Date(value as string);
    return date.toISOString();
  }

  static toEditAssetItemDtos(
    rows: Record<string, string>[],
    ids: string[],
  ): EditAssetItemDto[] {
    return rows.map((row, i) => ({
      id: ids[i] || undefined,
      assetName: row['assetName'] || undefined,
      propertyRegistryNumber: row['propertyRegistryNumber'] || undefined,
      propertyRegistryEnumValue: row['propertyRegistryEnumValue'] || undefined,
      assetUse: row['assetUse'] || undefined,
      cadastralReferences: row['cadastralReferences'] ? [row['cadastralReferences']] : undefined,
      idufir: row['idufir'] || undefined,
      adoptedValorationMethod: row['adoptedValorationMethod'] ? Number(row['adoptedValorationMethod']) : undefined,
      adoptedSurfaceValue: row['adoptedSurfaceValue'] ? Number(row['adoptedSurfaceValue']) : undefined,
      adoptedSurface: row['adoptedSurface'] ? Number(row['adoptedSurface']) : undefined,
      unitValue: row['unitValue'] ? Number(row['unitValue']) : undefined,
      totalValue: row['totalValue'] ? Number(row['totalValue']) : undefined,
    }));
  }
}
